<script setup>
/**
 * Which call outcomes get recorded into Smarticks, per PBX queue.
 * Plan: docs/smarticks/voipe-queue-events-to-automations-plan.md
 *
 * Queues are DISCOVERED from the switchboard, never a hand-kept list — a queue created on the PBX
 * has to appear here on its own, or the feature needs a developer per queue.
 *
 * 🔴 The 90-day counts are the point of the screen, not decoration. Whether a missed call shows up
 * as "hung up" or "waited too long" depends on that queue's own Max Wait, which the admin cannot
 * see and should not have to know: on queue 801 there were 121 timeouts against 36 abandons, on 799
 * it is 267 abandons and no timeouts at all. The numbers make the right box obvious without ever
 * mentioning Asterisk.
 *
 * 🔴 Explicit save, never save-on-click. A tick starts creating conversations, and on a busy queue
 * that is hundreds of them — there has to be a point of no-regret and a route-leave guard.
 */
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import SmarticksAPI from 'dashboard/api/smarticks';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const OUTCOMES = ['abandoned', 'timeout', 'answered'];

const loading = ref(true);
const saving = ref(false);
const failed = ref(false);
const unreachable = ref(false);
const queues = ref([]);
const selection = ref({});
const original = ref('');
const filter = ref('');

const isDirty = computed(
  () => JSON.stringify(selection.value) !== original.value
);

const visibleQueues = computed(() => {
  const term = filter.value.trim().toLowerCase();
  if (!term) return queues.value;
  return queues.value.filter(q => String(q.id).toLowerCase().includes(term));
});

const enabledCount = computed(() =>
  Object.values(selection.value).reduce((total, list) => total + list.length, 0)
);

const isOn = (queue, outcome) =>
  (selection.value[queue] || []).includes(outcome);

const toggle = (queue, outcome) => {
  const current = selection.value[queue] || [];
  selection.value = {
    ...selection.value,
    [queue]: current.includes(outcome)
      ? current.filter(o => o !== outcome)
      : [...current, outcome],
  };
};

// A queue whose missed calls mostly end as timeouts, where the admin has only ticked "hung up".
// Silently under-covering by ~77% is exactly the mistake this screen exists to prevent.
const underCovered = queue => {
  if (!isOn(queue.id, 'abandoned') || isOn(queue.id, 'timeout')) return false;
  return (queue.counts?.timeout || 0) > (queue.counts?.abandoned || 0);
};

const apply = payload => {
  unreachable.value = payload.unreachable;
  queues.value = payload.queues || [];
  const next = {};
  (payload.matrix || []).forEach(({ queue, outcome }) => {
    next[queue] = [...(next[queue] || []), outcome];
  });
  selection.value = next;
  original.value = JSON.stringify(next);
};

const load = async () => {
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await SmarticksAPI.getVoiceQueues();
    apply(data);
  } catch (error) {
    // NEVER an empty table: a failed load rendered as "no queues" is a wrong answer presented as a
    // fact, which is the exact scar the agent-endpoints screen carries.
    failed.value = true;
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await SmarticksAPI.updateVoiceQueues(selection.value);
    apply(data);
    useAlert('הגדרות התורים נשמרו. התיעוד מתחיל מעכשיו.');
  } catch (error) {
    useAlert('לא הצלחנו לשמור את הגדרות התורים.');
  } finally {
    saving.value = false;
  }
};

onMounted(load);
defineExpose({ isDirty });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="loading" class="flex flex-col gap-2">
      <div
        v-for="n in 4"
        :key="n"
        class="h-12 rounded-lg animate-pulse bg-n-alpha-1"
      />
    </div>

    <template v-else>
      <Banner
        v-if="failed"
        color="amber"
        :action-label="$t('SMARTICKS.VOICE_QUEUES.RETRY')"
        @action="load"
      >
        {{ $t('SMARTICKS.VOICE_QUEUES.LOAD_FAILED') }}
      </Banner>
      <Banner v-else-if="unreachable" color="amber">
        {{ $t('SMARTICKS.VOICE_QUEUES.PBX_UNREACHABLE') }}
      </Banner>
      <Banner v-else color="blue">
        {{ $t('SMARTICKS.VOICE_QUEUES.EXPLAINER') }}
      </Banner>

      <div v-if="queues.length > 6" class="max-w-xs">
        <Input
          v-model="filter"
          size="md"
          :placeholder="$t('SMARTICKS.VOICE_QUEUES.FILTER')"
        />
      </div>

      <p v-if="!queues.length && !failed" class="text-sm text-n-slate-11">
        {{ $t('SMARTICKS.VOICE_QUEUES.EMPTY') }}
      </p>

      <div
        v-for="queue in visibleQueues"
        :key="queue.id"
        class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-weak dark:outline-n-strong"
      >
        <!-- Two <bdi> elements, never one string: "801 support" in an RTL cell renders as
             "support 801" — the name of a queue that does not exist. -->
        <div class="flex items-center gap-2 min-w-0">
          <bdi class="text-xs shrink-0 font-inter tabular-nums text-n-slate-11">
            {{ queue.id }}
          </bdi>
        </div>

        <div class="flex flex-col gap-2 sm:flex-row sm:gap-6">
          <label
            v-for="outcome in OUTCOMES"
            :key="outcome"
            class="flex items-start gap-2 min-h-11 sm:min-h-0 cursor-pointer"
          >
            <Checkbox
              :model-value="isOn(queue.id, outcome)"
              class="mt-0.5"
              @update:model-value="toggle(queue.id, outcome)"
            />
            <span class="flex flex-col">
              <span class="text-sm text-n-slate-12">
                {{
                  $t(`SMARTICKS.VOICE_QUEUES.OUTCOME.${outcome.toUpperCase()}`)
                }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  $t(
                    `SMARTICKS.VOICE_QUEUES.OUTCOME_HINT.${outcome.toUpperCase()}`
                  )
                }}
              </span>
              <!-- Evidence, not decoration: this is what makes the right box obvious without
                   the admin needing to know what Max Wait is. -->
              <span class="text-xs text-n-slate-11 tabular-nums" dir="auto">
                {{
                  $t('SMARTICKS.VOICE_QUEUES.COUNT', {
                    count: queue.counts?.[outcome] || 0,
                  })
                }}
              </span>
            </span>
          </label>
        </div>

        <p
          v-if="underCovered(queue)"
          class="text-sm text-n-amber-11"
          dir="auto"
        >
          {{
            $t('SMARTICKS.VOICE_QUEUES.UNDER_COVERED', {
              count: queue.counts?.timeout || 0,
            })
          }}
        </p>
      </div>

      <div
        v-if="isDirty"
        class="sticky bottom-0 flex flex-wrap items-center gap-3 py-3 border-t bg-n-background border-n-weak"
      >
        <Button
          :label="$t('SMARTICKS.VOICE_QUEUES.SAVE')"
          :is-loading="saving"
          @click="save"
        />
        <span class="text-sm text-n-slate-11">
          {{ $t('SMARTICKS.VOICE_QUEUES.PENDING', { count: enabledCount }) }}
        </span>
      </div>
    </template>
  </div>
</template>
